import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { professionalClientsService } from '../../services/professionalClientsService';
import { Package, FileText, Upload, AlertCircle, CheckCircle, Clock, Filter, X } from 'lucide-react';
import Button from '../../components/ui/Button';

export default function ProfessionalOrdersProcessingCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'ex1'

  // Orders state
  const [pendingOrders, setPendingOrders] = useState([]);
  const [orderFilters, setOrderFilters] = useState({
    status: 'all',
    orderType: 'all',
    needsEx1: false
  });

  // EX1 state
  const [ex1Certificates, setEx1Certificates] = useState([]);
  const [ex1Filters, setEx1Filters] = useState({
    status: 'all',
    alertOnly: false
  });

  // Document upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentType, setDocumentType] = useState('invoice');
  const [documentMetadata, setDocumentMetadata] = useState({});

  useEffect(() => {
    loadData();
  }, [orderFilters, ex1Filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [orders, certificates] = await Promise.all([
        professionalClientsService?.getPendingOrders(orderFilters),
        professionalClientsService?.getEX1Certificates(ex1Filters)
      ]);

      setPendingOrders(orders);
      setEx1Certificates(certificates);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus, additionalData = {}) => {
    try {
      await professionalClientsService?.updateOrderStatus(orderId, newStatus, additionalData);
      await loadData();
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleUpdateEX1Status = async (certId, updates) => {
    try {
      await professionalClientsService?.updateEX1Status(certId, updates);
      await loadData();
    } catch (err) {
      console.error('Error updating EX1 status:', err);
      alert('Erreur lors de la mise à jour du certificat EX1');
    }
  };

  const handleDocumentUpload = async (file) => {
    if (!selectedOrder || !file) return;

    try {
      setUploadingDocument(true);

      const metadata = {
        documentType,
        shipmentId: selectedOrder?.shipmentId || null,
        ...documentMetadata
      };

      await professionalClientsService?.uploadDocument(
        selectedOrder?.professionalClientId,
        file,
        metadata
      );

      alert('Document téléversé avec succès!');
      setShowUploadModal(false);
      setSelectedOrder(null);
      setDocumentMetadata({});
      await loadData();
    } catch (err) {
      console.error('Error uploading document:', err);
      alert('Erreur lors du téléversement: ' + err?.message);
    } finally {
      setUploadingDocument(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      new: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      quote_sent: 'bg-purple-100 text-purple-800',
      confirmed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      preparation: 'bg-gray-100 text-gray-800',
      customs_approved: 'bg-green-100 text-green-800'
    };
    return statusClasses?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouveau',
      processing: 'En cours',
      quote_sent: 'Devis envoyé',
      confirmed: 'Confirmé',
      rejected: 'Rejeté',
      preparation: 'En préparation',
      customs_approved: 'Visé douane'
    };
    return labels?.[status] || status;
  };

  const getOrderTypeLabel = (type) => {
    const labels = {
      quote_volume: 'Devis volume',
      quote_barrels: 'Devis barriques',
      quote_container: 'Devis conteneur',
      order: 'Commande'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BreadcrumbTrail
          items={[
            { label: 'Admin', path: '/comprehensive-admin-kpi-dashboard' },
            { label: 'Clients Pro', path: '/professional-clients-management-dashboard' },
            { label: 'Traitement Commandes', path: '/professional-orders-processing-center' }
          ]}
          customItems={[]}
        />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Centre de Traitement Commandes Pro
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestion des commandes professionnelles et certificats EX1
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 dark:border-slate-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'orders' ?'border-green-500 text-green-600 dark:text-green-400' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Package className="w-5 h-5" />
                <span>Commandes en attente</span>
                <span className="ml-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full text-xs">
                  {pendingOrders?.length}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('ex1')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ex1' ?'border-green-500 text-green-600 dark:text-green-400' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Certificats EX1</span>
                <span className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-1 rounded-full text-xs">
                  {ex1Certificates?.filter(c => c?.alertDaysPending >= 7)?.length}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Filter className="w-5 h-5 mr-2" />
                  Filtres
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Statut
                  </label>
                  <select
                    value={orderFilters?.status}
                    onChange={(e) => setOrderFilters({ ...orderFilters, status: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="new">Nouveau</option>
                    <option value="processing">En cours</option>
                    <option value="quote_sent">Devis envoyé</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="rejected">Rejeté</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type de demande
                  </label>
                  <select
                    value={orderFilters?.orderType}
                    onChange={(e) => setOrderFilters({ ...orderFilters, orderType: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="all">Tous les types</option>
                    <option value="quote_volume">Devis volume</option>
                    <option value="quote_barrels">Devis barriques</option>
                    <option value="quote_container">Devis conteneur</option>
                    <option value="order">Commande</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orderFilters?.needsEx1}
                      onChange={(e) => setOrderFilters({ ...orderFilters, needsEx1: e?.target?.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Besoin EX1 uniquement</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Orders List */}
            {loading ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement des commandes...</p>
              </div>
            ) : pendingOrders?.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Aucune commande en attente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOrders?.map((order) => (
                  <div
                    key={order?.id}
                    className="bg-white dark:bg-slate-800 rounded-lg shadow hover:shadow-md transition-shadow p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {order?.professionalClient?.companyName || 'Client inconnu'}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(order?.status)}`}>
                            {getStatusLabel(order?.status)}
                          </span>
                          {order?.needsEx1 && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              Besoin EX1
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {order?.professionalClient?.contactName} • {order?.professionalClient?.email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(order.createdAt)?.toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de demande</p>
                        <p className="text-sm text-gray-900 dark:text-white">{getOrderTypeLabel(order?.orderType)}</p>
                      </div>
                      {order?.volumeRequested && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Volume demandé</p>
                          <p className="text-sm text-gray-900 dark:text-white">{order?.volumeRequested} L</p>
                        </div>
                      )}
                      {order?.quantityRequested && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantité</p>
                          <p className="text-sm text-gray-900 dark:text-white">{order?.quantityRequested}</p>
                        </div>
                      )}
                      {order?.estimatedAmount && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Montant estimé</p>
                          <p className="text-sm text-gray-900 dark:text-white">{order?.estimatedAmount} €</p>
                        </div>
                      )}
                    </div>

                    {order?.pickupAddress && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adresse d'enlèvement</p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {order?.pickupAddress}, {order?.pickupCity} {order?.pickupPostalCode}
                        </p>
                      </div>
                    )}

                    {order?.specialInstructions && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instructions spéciales</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{order?.specialInstructions}</p>
                      </div>
                    )}

                    <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                      <Button
                        onClick={() => handleUpdateOrderStatus(order?.id, 'processing')}
                        disabled={order?.status !== 'new'}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Traiter
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedOrder(order);
                          setDocumentType('invoice');
                          setShowUploadModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Uploader document
                      </Button>
                      <Button
                        onClick={() => handleUpdateOrderStatus(order?.id, 'confirmed')}
                        disabled={order?.status === 'confirmed'}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirmer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* EX1 Tab */}
        {activeTab === 'ex1' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Filter className="w-5 h-5 mr-2" />
                  Filtres
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Statut
                  </label>
                  <select
                    value={ex1Filters?.status}
                    onChange={(e) => setEx1Filters({ ...ex1Filters, status: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="preparation">En préparation</option>
                    <option value="processing">En cours de traitement</option>
                    <option value="customs_approved">Visé par la douane</option>
                    <option value="rejected">Rejeté</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ex1Filters?.alertOnly}
                      onChange={(e) => setEx1Filters({ ...ex1Filters, alertOnly: e?.target?.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Alertes uniquement (≥7 jours)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* EX1 List */}
            {loading ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement des certificats...</p>
              </div>
            ) : ex1Certificates?.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Aucun certificat EX1 à traiter</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ex1Certificates?.map((cert) => (
                  <div
                    key={cert?.id}
                    className={`bg-white dark:bg-slate-800 rounded-lg shadow hover:shadow-md transition-shadow p-6 ${
                      cert?.alertDaysPending >= 7 ? 'border-l-4 border-red-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {cert?.certificateNumber || 'Certificat sans numéro'}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(cert?.status)}`}>
                            {getStatusLabel(cert?.status)}
                          </span>
                          {cert?.alertDaysPending >= 7 && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 flex items-center">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {cert?.alertDaysPending} jours en attente
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {cert?.professionalClient?.companyName || 'Client inconnu'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {cert?.shipment && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expédition</p>
                          <p className="text-sm text-gray-900 dark:text-white">{cert?.shipment?.reference}</p>
                        </div>
                      )}
                      {cert?.submissionDate && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de soumission</p>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {new Date(cert.submissionDate)?.toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {cert?.customsOffice && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bureau de douane</p>
                          <p className="text-sm text-gray-900 dark:text-white">{cert?.customsOffice}</p>
                        </div>
                      )}
                    </div>

                    {cert?.rejectionReason && (
                      <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Raison du rejet</p>
                        <p className="text-sm text-red-600 dark:text-red-400">{cert?.rejectionReason}</p>
                      </div>
                    )}

                    <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                      <Button
                        onClick={() => handleUpdateEX1Status(cert?.id, { status: 'processing' })}
                        disabled={cert?.status !== 'preparation'}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Traiter
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedOrder({ ...cert, professionalClientId: cert?.professionalClientId });
                          setDocumentType('ex1_certificate');
                          setShowUploadModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Uploader EX1 visé
                      </Button>
                      <Button
                        onClick={() => handleUpdateEX1Status(cert?.id, { status: 'customs_approved', approvalDate: new Date()?.toISOString() })}
                        disabled={cert?.status === 'customs_approved'}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approuver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Téléverser un document
                </h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedOrder(null);
                    setDocumentMetadata({});
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type de document
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="invoice">Facture</option>
                    <option value="ex1_certificate">Certificat EX1</option>
                    <option value="photo">Photo</option>
                    <option value="bill_of_lading">Connaissement</option>
                    <option value="attestation">Attestation</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                {documentType === 'invoice' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Numéro de facture
                      </label>
                      <input
                        type="text"
                        value={documentMetadata?.invoiceNumber || ''}
                        onChange={(e) => setDocumentMetadata({ ...documentMetadata, invoiceNumber: e?.target?.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                        placeholder="ADT-2026-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Montant TTC (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={documentMetadata?.invoiceAmountTtc || ''}
                        onChange={(e) => setDocumentMetadata({ ...documentMetadata, invoiceAmountTtc: parseFloat(e?.target?.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                        placeholder="1440.00"
                      />
                    </div>
                  </>
                )}

                {documentType === 'ex1_certificate' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Numéro de certificat
                    </label>
                    <input
                      type="text"
                      value={documentMetadata?.ex1CertificateNumber || ''}
                      onChange={(e) => setDocumentMetadata({ ...documentMetadata, ex1CertificateNumber: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                      placeholder="EX1-2026-001"
                    />
                  </div>
                )}

                {documentType === 'photo' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Légende
                    </label>
                    <input
                      type="text"
                      value={documentMetadata?.photoCaption || ''}
                      onChange={(e) => setDocumentMetadata({ ...documentMetadata, photoCaption: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                      placeholder="Chargement du conteneur"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fichier
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                      const file = e?.target?.files?.[0];
                      if (file) {
                        handleDocumentUpload(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                    disabled={uploadingDocument}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    PDF, JPG, PNG ou WEBP (max 20MB)
                  </p>
                </div>

                {uploadingDocument && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">Téléversement en cours...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}